import dayjs from "dayjs"

import { OPERATIONAL_STATES } from "../../../../Lead/constants/operational-states.ts"
import {
  matchesActiveFilters,
  narrowsAnything
} from "../../../redux/leads.constants.ts"
import type { LeadsFilters } from "../../../redux/leads.types.ts"
import type { LeadKpiIconKey } from "./lead-kpi-icons.tsx"

/**
 * How each KPI is drawn, keyed by the tile id the payload carries.
 *
 * The endpoint sends data — id, title, value, comparison — and nothing about
 * appearance. Glyph and colour are presentation and belong here, so the API
 * never has to describe how the client paints itself, and a tile the client
 * has never heard of still renders rather than falling back to a wrong glyph.
 *
 * The three tiles the client has not yet defined (PNS, Long Parked, Exchange
 * Opps) are registered ahead of time from the design, so they arrive fully
 * styled the moment the backend starts sending them.
 */
export interface LeadKpiPresentation {
  icon: LeadKpiIconKey
  tint: string
  /**
   * Whether a rise is good news. Decides the colour of the trend, which the
   * arrow alone cannot: more leads lost is a rise and bad, fewer is a fall and
   * good. The design colours each tile by this rule — Long Parked and
   * Closed-Lost show green on a decrease, every other tile on an increase.
   */
  higherIsBetter?: boolean
  /** Where the tile drills to, as a `/leads` query the page can hydrate. */
  drillTo?: string
}

/**
 * The narrowing a tile drills to, resolved per call rather than stored. Two of
 * the four tiles narrow by a date the user is standing on, and a module-level
 * constant would freeze it at the moment the bundle was evaluated — a tab left
 * open overnight would drill to yesterday.
 */
interface LeadKpiSpec extends Omit<LeadKpiPresentation, "drillTo"> {
  /**
   * Stated as filters rather than query params so the tile's selected state and
   * the URL it drills to cannot disagree — the query string is serialized from
   * this, and the same object is compared against the live filters.
   */
  drillFilters?: () => Partial<LeadsFilters>
}

const day = (value: dayjs.Dayjs): string => value.format("YYYY-MM-DD")

const drill = (id: string, filters: Partial<LeadsFilters>): string => {
  const params = new URLSearchParams({ kpi: id })

  Object.entries(filters).forEach(([key, value]) => {
    if (value === null || value === undefined) return
    params.set(key, Array.isArray(value) ? value.join(",") : String(value))
  })

  return `/leads?${params.toString()}`
}

const LEAD_KPI_SPECS: Record<string, LeadKpiSpec> = {
  // The figure counts the unfiltered pipeline, so the drill narrows by nothing
  // — the `kpi` marker alone is what clears whatever the user left behind.
  active_leads: { icon: "people", tint: "#3B82F6", drillFilters: () => ({}) },
  hot_leads: {
    icon: "flame",
    tint: "#EF4444",
    drillFilters: () => ({ operational_states: [OPERATIONAL_STATES.HOT] })
  },
  pns_not_sold: { icon: "funnel", tint: "#8B5CF6" },
  // The tile counts `follow_up_date <= today`, so it includes what is overdue
  // as well as what is due. An `after` bound would drill to the same-day slice
  // and land the user on fewer rows than the figure they clicked.
  follow_ups_today: {
    icon: "clock",
    tint: "#F59E0B",
    drillFilters: () => ({ follow_up_date_before: day(dayjs()) })
  },
  long_parked: { icon: "clock", tint: "#F59E0B", higherIsBetter: false },
  // Deliberately not clickable. The tile counts `is_closed_lost` stamped inside
  // the current month, and the list can express none of that: there is no
  // `is_closed_lost` or `archived_at` filter, and `status=lost` is a different
  // population — the lifecycle migration leaves closed-lost leads on the active
  // stage they died at. It would also drill to an empty table for anyone
  // without `lead_view_archive`, who still sees a non-zero figure. Restore the
  // drill once the backend exposes the two filters or sends its own `drill_to`.
  closed_lost_month: { icon: "xcircle", tint: "#EF4444", higherIsBetter: false },
  exchange_opps: { icon: "exchange", tint: "#22C55E" }
}

// A neutral glyph and no destination beat a wrong glyph and a wrong filter,
// which is what an unrecognised tile would otherwise inherit.
const FALLBACK: LeadKpiSpec = { icon: "people", tint: "#64748B" }

/**
 * Retained for the polarity table the compare chip reads. The drill target is
 * not on it — it is resolved by `leadKpiPresentation`, which has a clock.
 */
export const LEAD_KPI_PRESENTATION: Record<string, LeadKpiSpec> = LEAD_KPI_SPECS

export const leadKpiPresentation = (id: string): LeadKpiPresentation => {
  const { drillFilters, ...presentation } = LEAD_KPI_SPECS[id] ?? FALLBACK

  return drillFilters
    ? { ...presentation, drillTo: drill(id, drillFilters()) }
    : presentation
}

/**
 * Whether the table is currently showing exactly this tile's population.
 *
 * Read from the live filters rather than from whichever tile was clicked last,
 * so a preset chip — or a filter the user edits by hand — that replaces the
 * narrowing un-selects the tile without the two rows knowing about each other.
 *
 * A tile that drills to no narrowing at all (Active Leads) is never selected:
 * its target matches an unfiltered page, so it would light up on load while
 * describing nothing the user chose.
 */
export const isLeadKpiActive = (
  id: string,
  filters: LeadsFilters | null | undefined
): boolean => {
  const drillFilters = LEAD_KPI_SPECS[id]?.drillFilters

  if (!drillFilters) return false

  const target = drillFilters()

  return narrowsAnything(target) && matchesActiveFilters(target, filters)
}
